import React, { useState, useRef, useEffect } from 'react';
import './searchableSelect.css';

const SearchableSelect = ({ 
  options = [],
  value, 
  onChange, 
  placeholder, 
  displayKey, 
  valueKey,
  required = false,
  name = '',
  // Optional second line under each result — company, email, phone. Contacts from
  // one agency have near-identical names, so the label alone does not say which
  // person you are about to pick. Omit it and the control behaves exactly as before.
  detailKeys = [],
  // Optional "the thing you searched for does not exist yet" escape hatch. Given a
  // handler, the result list ends with a create row carrying whatever was typed.
  //
  // Opt-in so every other SearchableSelect behaves exactly as before — offering to
  // create a sales rep or a lead source from a dropdown would be wrong.
  onCreateNew = null,
  createLabel = 'Create',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const containerRef = useRef(null);

  useEffect(() => {
    const filtered = options.filter(option => {
      if (!option || !option[displayKey]) return false;

      const search = searchTerm.toLowerCase();
      if (option[displayKey].toString().toLowerCase().includes(search)) return true;

      // Also match on the detail fields, so a phone number or email finds someone
      // whose name you cannot remember or cannot spell.
      return detailKeys.some(k =>
        option[k] && option[k].toString().toLowerCase().includes(search));
    });
    setFilteredOptions(filtered);
  }, [searchTerm, options, displayKey, detailKeys]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options && options.length > 0 
    ? options.find(option => option && option[valueKey] === value) 
    : null;

  const handleSelect = (selectedValue) => {
    const event = {
      target: {
        name: name,
        value: selectedValue
      }
    };
    onChange(event);
    setSearchTerm('');
    setIsOpen(false);
  };

  return (
    <div className="searchable-select" ref={containerRef}>
      {/* Hidden input for form validation */}
      <input
        type="hidden"
        name={name}
        value={value || ''}
        required={required}
      />
      
      <input
        type="text"
        className="form-input"
        placeholder={selectedOption ? selectedOption[displayKey] : placeholder}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onFocus={() => setIsOpen(true)}
        autoComplete="off"
      />
      
      {isOpen && (
        <div className="searchable-options">
          {value && (
            <div
              className="searchable-option clear-option"
              onClick={() => handleSelect('')}
            >
              <em>Clear selection</em>
            </div>
          )}

          {/* Don't dump the entire list on click. Wait for the user to type. */}
          {!searchTerm && options.length > 0 && (
            <div className="searchable-option disabled">
              Start typing to search…
            </div>
          )}

          {searchTerm && filteredOptions.slice(0, 10).map((option) => (
            <div
              key={option[valueKey]}
              className="searchable-option"
              onClick={() => handleSelect(option[valueKey])}
            >
              <div>{option[displayKey]}</div>
              {detailKeys.length > 0 && (() => {
                const detail = detailKeys.map(k => option[k]).filter(Boolean).join(' · ');
                return detail ? <div className="searchable-option-detail">{detail}</div> : null;
              })()}
            </div>
          ))}

          {searchTerm && filteredOptions.length > 10 && (
            <div className="searchable-option disabled">
              ... {filteredOptions.length - 10} more results (keep typing to narrow down)
            </div>
          )}

          {searchTerm && filteredOptions.length === 0 && !onCreateNew && (
            <div className="searchable-option disabled">
              No results found for "{searchTerm}"
            </div>
          )}

          {/* Offered even when there ARE matches, not only on an empty result — two
              different people share a name often enough that "no results" is the
              wrong moment to decide this is someone new. Last in the list, so it is
              never the thing you hit while aiming for a real match. */}
          {searchTerm && onCreateNew && (
            <div
              className="searchable-option create-option"
              onClick={() => {
                onCreateNew(searchTerm);
                setSearchTerm('');
                setIsOpen(false);
              }}
            >
              <div>
                <i className="fas fa-plus" style={{ marginRight: '6px' }}></i>
                {createLabel} "{searchTerm}"
              </div>
              {filteredOptions.length === 0 && (
                <div className="searchable-option-detail">No existing match</div>
              )}
            </div>
          )}

          {options.length === 0 && (
            <div className="searchable-option disabled">
              No options available
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;